import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {ProfileComparisonResultDeltaChip} from './ProfileComparisonResultDeltaChip';
import {profileComparisonSelection} from '../../../../../layouts/theme/designSystem';
import type {
  ProfileComparisonDimension,
  ProfileComparisonDimensionDelta,
  ProfileComparisonSummary,
} from '../../types/ProfileComparisonTypes';
import {PROFILE_COMPARISON_DIMENSIONS} from '../../types/ProfileComparisonTypes';

export type ProfileComparisonScoreCardSlot = 'a' | 'b';

export interface Props {
  slot: ProfileComparisonScoreCardSlot;
  summary: ProfileComparisonSummary;
  dimensionDeltas: ProfileComparisonDimensionDelta[];
}

const slotColors = {
  a: profileComparisonSelection.slotA,
  b: profileComparisonSelection.slotB,
} as const;

const getScoreForDimension = (
  summary: ProfileComparisonSummary,
  dimensionDeltas: ProfileComparisonDimensionDelta[],
  dimension: ProfileComparisonDimension,
  slot: ProfileComparisonScoreCardSlot,
): number | null => {
  if (dimension === 'overall') {
    return summary.overallScore;
  }

  // Non-overall scores come from dimensionDeltas, which already carries the real
  // per-dimension score (including soft skills) for both profiles.
  const entry = dimensionDeltas.find((item) => item.dimension === dimension);
  if (!entry) {
    return null;
  }

  return slot === 'a' ? entry.scoreA : entry.scoreB;
};

const getDeltaForSlot = (
  dimensionDeltas: ProfileComparisonDimensionDelta[],
  dimension: ProfileComparisonDimension,
  slot: ProfileComparisonScoreCardSlot,
): number | null => {
  const entry = dimensionDeltas.find((item) => item.dimension === dimension);
  if (!entry) {
    return null;
  }

  return slot === 'a' ? entry.deltaA : entry.deltaB;
};

export const ProfileComparisonResultScoreCard = ({slot, summary, dimensionDeltas}: Props) => {
  const {t} = useTranslation('translation', {
    keyPrefix: 'workforceDepartmentHealthPage.profileComparison.result',
  });
  const {t: tDimensions} = useTranslation('translation', {
    keyPrefix: 'workforceDepartmentHealthPage.scoreCards.dimensions',
  });

  const colors = slotColors[slot];
  const slotLabel = slot === 'a' ? t('profileALabel') : t('profileBLabel');

  return (
    <Box
      data-testid={`profile-comparison-score-card-${slot}`}
      sx={{
        border: '1.5px solid',
        borderColor: colors.borderColor,
        borderRadius: '12px',
        bgcolor: colors.bgcolor,
        px: 2,
        py: 1.75,
      }}
    >
      <Typography
        sx={{
          fontSize: '10px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          color: colors.color,
          mb: 0.5,
        }}
      >
        {slotLabel}
      </Typography>
      <Typography sx={{fontSize: '14px', fontWeight: 600, mb: 0.25}}>{summary.profileId}</Typography>
      <Typography sx={{fontSize: '12px', color: 'text.secondary', mb: 0.125}}>{summary.targetRole ?? '—'}</Typography>
      <Typography sx={{fontSize: '12px', color: 'text.secondary', mb: 1.5}}>
        {[summary.seniority, summary.department].filter(Boolean).join(' · ') || '—'}
      </Typography>

      {PROFILE_COMPARISON_DIMENSIONS.map((dimension) => {
        const score = getScoreForDimension(summary, dimensionDeltas, dimension, slot);
        const delta = getDeltaForSlot(dimensionDeltas, dimension, slot);

        return (
          <Box
            key={dimension}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.625,
              borderTop: dimension === 'overall' ? 'none' : '0.5px solid',
              borderColor: 'divider',
            }}
          >
            <Typography sx={{fontSize: '12px', color: 'text.secondary', flex: 1}}>
              {tDimensions(dimension)}
            </Typography>
            <Typography sx={{fontSize: '13px', fontWeight: 600, minWidth: 44, textAlign: 'right'}}>
              {score !== null ? `${score}%` : '—'}
            </Typography>
            <ProfileComparisonResultDeltaChip delta={delta} />
          </Box>
        );
      })}
    </Box>
  );
};
